/**
 * ExportService - Reusable PDF and Image export functionality
 * 
 * Usage example:
 * 
 * constructor(private exportService: ExportService) {}
 * 
 * // Generate PDF
 * this.exportService.generatePdf({
 *   elementId: 'my-widget-id',
 *   filename: 'my-report.pdf',
 *   pageSize: 'a4',
 *   orientation: 'portrait'
 * });
 * 
 * // Generate PNG
 * this.exportService.generateImage({
 *   elementId: 'my-widget-id',
 *   filename: 'my-report.png',
 *   quality: 1,
 *   pixelRatio: 2
 * });
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { jsPDF } from 'jspdf';
import * as htmlToImage from 'html-to-image';
import { AlertService } from '../sp-alert/alert.service';
import { environment } from 'src/environments/environment';

export interface ExportOptions {
  elementId: string;
  filename?: string;
  pageSize?: 'a4' | 'a3' | 'letter';
  orientation?: 'portrait' | 'landscape';
  margin?: number;
  backgroundColor?: string;
  quality?: number;
  pixelRatio?: number;
}

export interface TextPdfOptions {
  elementId: string;
  filename?: string;
  pageSize?: 'a4' | 'a3' | 'letter';
  orientation?: 'portrait' | 'landscape';
  margin?: number;
  fontSize?: number;
  lineHeight?: number;
}

export interface ElementExportResult {
  restore: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  
  private pdfApi = environment.config.pptx.api;

  constructor(
    private http: HttpClient,
    private alert: AlertService
  ) { }

  /**
   * Generate PDF from an HTML element by ID
   * @param options Export configuration options
   * @returns Promise that resolves when export is complete
   */
  async generatePdf(options: ExportOptions): Promise<void> {
    const node: HTMLElement = document.getElementById(options.elementId);

    if (!node) {
      console.error(`Element with id "${options.elementId}" not found. Cannot export PDF.`);
      this.alert.notify('Error', 'Widget not found for export. Try again in a moment.', 'error');
      return;
    }

    // Prepare element for full content export
    const { restore } = this.prepareElementForFullExport(node);

    const doc = new jsPDF(
      options.orientation || 'p', 
      'px', 
      options.pageSize || 'a4'
    );
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = options.margin || 20;
    const contentWidth = pageWidth - (2 * margin);
    const contentHeight = pageHeight - (2 * margin);
    
    // Small delay to let the DOM update
    setTimeout(() => {
      const ratioW = contentWidth / node.offsetWidth;
      const contentTotalHeight = node.scrollHeight * ratioW;
      
      htmlToImage.toPng(node, {
        quality: options.quality || 1, 
        pixelRatio: options.pixelRatio || 1,
        width: node.offsetWidth,
        height: node.scrollHeight, // Use scrollHeight for full content
        backgroundColor: options.backgroundColor || 'white' // Add white background
      }).then(function (dataUrl) {
        var img = new Image();
        img.onload = function() {
          // Check if content fits on one page
          if (contentTotalHeight <= contentHeight) {
            // Content fits, use normal size
            doc.addImage(dataUrl, 'PNG', margin, margin, contentWidth, contentTotalHeight);
          } else {
            // Content is too tall, split across multiple pages
            const totalPages = Math.ceil(contentTotalHeight / contentHeight);
            const heightPerPage = contentHeight / ratioW; // Convert back to image coordinates
            
            for (let pageNum = 0; pageNum < totalPages; pageNum++) {
              if (pageNum > 0) {
                doc.addPage();
              }
              
              const pageHeightAvailable = contentHeight;
              const heightForThisPage = Math.min(heightPerPage * ratioW, pageHeightAvailable);
              
              // Create a canvas to crop the image for this page
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = node.offsetWidth;
              canvas.height = heightPerPage;
              
              // Draw the cropped portion of the image
              ctx.drawImage(img, 0, pageNum * heightPerPage, node.offsetWidth, heightPerPage, 0, 0, canvas.width, canvas.height);
              
              // Convert canvas to data URL and add to PDF
              const pageDataUrl = canvas.toDataURL('image/png');
              doc.addImage(pageDataUrl, 'PNG', margin, margin, contentWidth, heightForThisPage);
            }
          }
          doc.save(options.filename || 'reporting.pdf');
        };
        img.src = dataUrl;
      })
      .catch(function (error) {
        console.error('oops, something went wrong!', error);
      })
      .finally(() => {
        // Always restore original styles
        restore();
      });
    }, 100);
  }

  /**
   * Generate PNG image from an HTML element by ID
   * @param options Export configuration options
   * @returns Promise that resolves when export is complete
   */
  async generateImage(options: ExportOptions): Promise<void> {
    const node: HTMLElement = document.getElementById(options.elementId);
    
    if (!node) {
      console.error(`Element with id "${options.elementId}" not found. Cannot export image.`);
      this.alert.notify('Error', 'Widget not found for export. Try again in a moment.', 'error');
      return;
    }
    
    // Prepare element for full content export
    const { restore } = this.prepareElementForFullExport(node);
    
    // Small delay to let the DOM update
    setTimeout(() => {
      htmlToImage.toPng(node, {
        quality: options.quality || 1, 
        pixelRatio: options.pixelRatio || 2,
        width: node.offsetWidth,
        height: node.scrollHeight, // Use scrollHeight for full content
        backgroundColor: options.backgroundColor || 'white' // Add white background
      }).then(function (dataUrl) {
        var img = new Image();
        img.onload = function() {
          // Create a canvas with padding
          const margin = options.margin || 40;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set canvas size with padding
          canvas.width = img.width + (2 * margin);
          canvas.height = img.height + (2 * margin);
          
          // Fill background
          ctx.fillStyle = options.backgroundColor || 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw the image with padding
          ctx.drawImage(img, margin, margin);
          
          // Convert to data URL and download
          const paddedDataUrl = canvas.toDataURL('image/png');
          
          // Use downloadjs if available, otherwise create download link
          if (typeof (window as any).download === 'function') {
            (window as any).download(paddedDataUrl, options.filename || 'reporting.png');
          } else {
            const a = document.createElement('a');
            a.href = paddedDataUrl;
            a.download = options.filename || 'reporting.png';
            a.click();
          }
        };
        img.src = dataUrl;
      })
      .catch(function (error) {
        console.error('oops, something went wrong!', error);
      })
      .finally(() => {
        // Always restore original styles
        restore();
      });
    }, 100);
  }

  /**
   * Generate PDF from text content using jsPDF text rendering
   * This function is specifically designed for text-heavy content like AI reports
   * @param options Text PDF configuration options
   * @returns Promise that resolves when export is complete
   */
  async generateTextPdf(options: TextPdfOptions): Promise<void> {
    const node: HTMLElement = document.getElementById(options.elementId);

    if (!node) {
      console.error(`Element with id "${options.elementId}" not found. Cannot export PDF.`);
      this.alert.notify('Error', 'Content not found for export. Try again in a moment.', 'error');
      return;
    }

    const doc = new jsPDF(
      options.orientation || 'p', 
      'mm', 
      options.pageSize || 'a4'
    );
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = options.margin || 20;
    const fontSize = options.fontSize || 12;
    const lineHeight = options.lineHeight || 1.2;
    
    const contentWidth = pageWidth - (2 * margin);
    const contentHeight = pageHeight - (2 * margin);
    
    // Set font and size
    doc.setFontSize(fontSize);
    
    // Convert HTML to plain text while preserving basic structure
    const textContent = this.htmlToPlainText(node);
    const lines = doc.splitTextToSize(textContent, contentWidth);
    
    let currentY = margin;
    let currentPage = 1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineHeightInMm = fontSize * lineHeight * 0.3527777778; // Convert pt to mm
      
      // Check if we need a new page
      if (currentY + lineHeightInMm > pageHeight - margin) {
        doc.addPage();
        currentPage++;
        currentY = margin;
      }
      
      // Add the line to the PDF
      doc.text(line, margin, currentY);
      currentY += lineHeightInMm;
    }
    
    doc.save(options.filename || 'ai-report-summary.pdf');
  }

  /**
   * Convert HTML content to plain text while preserving basic structure
   * @param element The HTML element to convert
   * @returns Plain text with basic formatting preserved
   */
  private htmlToPlainText(element: HTMLElement): string {
    let text = '';
    
    // Handle different HTML elements
    for (let i = 0; i < element.childNodes.length; i++) {
      const node = element.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent || '';
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
        
        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            text += '\n\n' + (element.textContent || '').trim() + '\n\n';
            break;
          case 'p':
            text += '\n\n' + (element.textContent || '').trim() + '\n\n';
            break;
          case 'br':
            text += '\n';
            break;
          case 'li':
            text += '• ' + (element.textContent || '').trim() + '\n';
            break;
          case 'ul':
          case 'ol':
            text += '\n' + this.htmlToPlainText(element) + '\n';
            break;
          case 'div':
            text += this.htmlToPlainText(element);
            break;
          default:
            text += element.textContent || '';
        }
      }
    }
    
    // Clean up extra whitespace
    return text.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
  }

  /**
   * Temporarily expands a scrollable element to show full content for export
   * @param element The DOM element to expand
   * @returns Object with original styles and restore function
   */
  private prepareElementForFullExport(element: HTMLElement): ElementExportResult {
    const originalStyles = {
      overflow: element.style.overflow,
      overflowY: element.style.overflowY,
      overflowX: element.style.overflowX,
      height: element.style.height,
      maxHeight: element.style.maxHeight
    };

    // Set element to show full content
    element.style.overflow = 'visible';
    element.style.overflowY = 'visible';
    element.style.overflowX = 'visible';
    element.style.height = element.scrollHeight + 'px';
    element.style.maxHeight = 'none';

    // Also handle any child elements that might be scrollable
    const scrollableChildren = element.querySelectorAll('[style*="overflow"], .overflow-y-scroll, .overflow-scroll');
    const childOriginalStyles: { element: HTMLElement, styles: any }[] = [];
    
    scrollableChildren.forEach((child: HTMLElement) => {
      const childStyles = {
        overflow: child.style.overflow,
        overflowY: child.style.overflowY,
        overflowX: child.style.overflowX,
        height: child.style.height,
        maxHeight: child.style.maxHeight
      };
      childOriginalStyles.push({ element: child, styles: childStyles });
      
      child.style.overflow = 'visible';
      child.style.overflowY = 'visible';
      child.style.overflowX = 'visible';
      if (child.scrollHeight > child.clientHeight) {
        child.style.height = child.scrollHeight + 'px';
      }
      child.style.maxHeight = 'none';
    });

    return {
      restore: () => {
        // Restore main element
        element.style.overflow = originalStyles.overflow;
        element.style.overflowY = originalStyles.overflowY;
        element.style.overflowX = originalStyles.overflowX;
        element.style.height = originalStyles.height;
        element.style.maxHeight = originalStyles.maxHeight;
        
        // Restore child elements
        childOriginalStyles.forEach(({ element: child, styles }) => {
          child.style.overflow = styles.overflow;
          child.style.overflowY = styles.overflowY;
          child.style.overflowX = styles.overflowX;
          child.style.height = styles.height;
          child.style.maxHeight = styles.maxHeight;
        });
      }
    };
  }

  exportHtmlToPdf(html: string, css: string, filename: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.pdfApi}/html-to-pdf`, { html, css, filename })
        .subscribe({
          next: async (response: any) => {
            try {
              const pdfUrl = this.getPdfUrl(response, filename);
              await this.downloadPdf(pdfUrl, filename);
              resolve();
            } catch (error) {
              this.handleError('download', error, reject);
            }
          },
          error: (error) => this.handleError('generate', error, reject)
        });
    });
  }

  private getPdfUrl(response: any, filename: string): string {
    return response?.downloadUrl || response?.pdfUrl || `${this.pdfApi}/pdf/files/${filename}`;
  }

  private async downloadPdf(pdfUrl: string, filename: string): Promise<void> {
    await this.downloadFileFromUrl(pdfUrl, filename);
  }

  /**
   * Downloads a file from a URL, with CORS fallback
   * Attempts to fetch as blob first (works with CORS), falls back to opening in new tab
   */
  public async downloadFileFromUrl(url: string, filename: string): Promise<void> {
    try {
      // Try to fetch the file as a blob and download it (works if CORS headers are set)
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      this.triggerDownload(blob, filename);
    } catch (error) {
      // Fallback: use anchor tag with target="_blank" to avoid popup blocker
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = url;
      a.target = '_blank';
      a.download = filename;
      a.click();
      a.remove();
    }
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    window.URL.revokeObjectURL(blobUrl);
  }

  private handleError(action: string, error: any, reject: (reason?: any) => void): void {
    console.error(`Error ${action}ing PDF:`, error);
    this.alert.notify('Error', `Failed to ${action} PDF. Please try again.`, 'error');
    reject(error);
  }

  /**
   * Export AI widget to PDF
   */
  async exportAiWidgetToPdf(widgetData: any, projectName: string): Promise<void> {
    return this.exportAiWidget(widgetData, projectName, 'pdf');
  }

  /**
   * Export AI widget to PNG
   */
  async exportAiWidgetToPng(widgetData: any, projectName: string): Promise<void> {
    return this.exportAiWidget(widgetData, projectName, 'png');
  }

  /**
   * Unified AI widget export method - DRY principle
   */
  private async exportAiWidget(
    widgetData: any, 
    projectName: string, 
    format: 'pdf' | 'png'
  ): Promise<void> {
   
    // Validate data once
    if (!this.isValidWidgetData(widgetData)) {
      this.alert.notify('Error', 'Widget data not loaded yet. Please wait for the widget to finish loading.', 'error');
      return;
    }

    try {
      // Generate HTML content (shared between PDF and PNG)
      const { html, css } = this.generateAiWidgetExportContent(widgetData);
      const filename = this.generateExportFilename(projectName, format);
      
     
      // Export using format-specific strategy
      if (format === 'pdf') {
        await this.exportHtmlToPdf(html, css, filename);
      } else {
        await this.exportHtmlToPng(html, css, filename);
      }

    } catch (error) {
      console.error(`❌ Error exporting AI widget to ${format.toUpperCase()}:`, error);
      this.alert.notify('Error', `${format.toUpperCase()} export failed: ${error.message}`, 'error');
    }
      
  }

  /**
   * Validates widget data
   */
  private isValidWidgetData(widgetData: any): boolean {
    return widgetData?.contentData && 
           (widgetData.contentData.headerTitle || 
            widgetData.contentData.headerSubtitle || 
            (widgetData.contentData.slides && widgetData.contentData.slides.length > 0));
  }

  /**
   * Generates export filename based on project name and format
   */
  private generateExportFilename(projectName: string, format: 'pdf' | 'png'): string {
    const cleanName = projectName.replace(/[^a-zA-Z0-9]/g, '_');
    return `AI_Widget_${cleanName}.${format}`;
  }

  /**
   * Generates HTML and CSS content for export (used by both PDF and PNG)
   */
  private generateAiWidgetExportContent(widgetData: any): { html: string, css: string } {
    return {
      html: this.createAiWidgetFlatExportHtml(widgetData),
      css: this.createAiWidgetExportCss()
    };
  }

  /**
   * Export HTML to PNG using html2canvas
   */
  private async exportHtmlToPng(html: string, css: string, filename: string): Promise<void> {
    const container = this.createOffscreenContainer('png-export-container');
    
    try {
      container.innerHTML = `<style>${css}</style>${html}`;
      document.body.appendChild(container);
      
      await this.waitForRendering();
      
      const canvas = await this.captureWithHtml2Canvas(container);
      this.downloadCanvas(canvas, filename);
      
    } finally {
      this.removeContainer(container);
    }
      

  }

  /**
   * Creates flat HTML for AI widget export with all slides stacked vertically
   */
  private createAiWidgetFlatExportHtml(widgetData: any): string {
    if (!widgetData?.contentData) {
      return '<div>AI Widget - Content not available</div>';
    }

    let html = `<div class="ai-widget-export">`;
    
    // Add title
    if (widgetData.contentData.headerTitle) {
      html += `<h1 class="widget-title">${widgetData.contentData.headerTitle}</h1>`;
    }
    
    if (widgetData.contentData.headerSubtitle) {
      html += `<p class="widget-subtitle">${widgetData.contentData.headerSubtitle}</p>`;
    }

    // Add all slides stacked vertically
    if (widgetData.contentData.slides && Array.isArray(widgetData.contentData.slides)) {
      widgetData.contentData.slides.forEach((slide: any, index: number) => {
        html += `<div class="slide-section">`;
        
        if (slide.title) {
          html += `<h2 class="slide-title">${slide.title}</h2>`;
        }
        
        if (slide.inspirationalContent && Array.isArray(slide.inspirationalContent)) {
          html += `<div class="inspirational-section">`;
          html += `<h3>Inspirational Content</h3>`;
          html += `<ul class="inspiration-list">`;
          slide.inspirationalContent.forEach((item: string) => {
            html += `<li>${item}</li>`;
          });
          html += `</ul></div>`;
        }
        
        if (slide.why) {
          html += `<div class="why-section">`;
          html += `<h3>Why?</h3>`;
          html += `<p>${slide.why}</p>`;
          html += `</div>`;
        }
        
        if (slide.impactGoal) {
          html += `<div class="impact-section">`;
          html += `<h3>Impact Goal</h3>`;
          html += `<p>${slide.impactGoal}</p>`;
          html += `</div>`;
        }
        
        html += `</div>`;
      });
    }

    html += `</div>`;
    return html;
  }

  /**
   * Creates CSS for AI widget export
   */
  private createAiWidgetExportCss(): string {
    return `

      .ai-widget-export {
        max-width: 800px;
        margin: 0 auto;
      }

      .widget-title {
        font-size: 28px;
        font-weight: 700;
        color: #333;
        margin: 0 0 15px 0;
        text-align: center;
      }

      .widget-subtitle {
        font-size: 16px;
        color: #666;
        margin: 0 0 30px 0;
        text-align: center;
      }

      .slide-section {
        margin-bottom: 40px;
        padding: 25px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        background: #fafafa;
        page-break-inside: avoid;
      }

      .slide-title {
        font-size: 22px;
        font-weight: 600;
        color: #333;
        margin: 0 0 20px 0;
        border-bottom: 2px solid #EF8251;
        padding-bottom: 8px;
      }

      h3 {
        font-size: 18px;
        font-weight: 600;
        color: #EF8251;
        margin: 20px 0 10px 0;
      }

      .inspirational-section,
      .why-section,
      .impact-section {
        margin-bottom: 20px;
      }

      .inspiration-list {
        margin: 10px 0 0 20px;
        padding: 0;
      }

      .inspiration-list li {
        margin-bottom: 8px;
        color: #333;
        line-height: 1.5;
      }

      p {
        margin: 10px 0;
        color: #333;
        line-height: 1.6;
      }

      @media print {
        .slide-section {
          page-break-inside: avoid;
          break-inside: avoid;
        }
      }
    `;
  }

  /**
   * Creates an off-screen container for rendering export content
   */
  private createOffscreenContainer(id: string): HTMLElement {
    const container = document.createElement('div');
    container.id = id;
    
    // Position completely off-screen but allow rendering
    container.style.position = 'fixed';
    container.style.top = '-10000px';
    container.style.left = '-10000px';
    container.style.width = '800px';
    container.style.height = 'auto';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    container.style.color = '#333';
    container.style.lineHeight = '1.6';
    container.style.boxSizing = 'border-box';
    container.style.overflow = 'hidden';
    container.style.zIndex = '-9999';
    
    return container;
  }

  /**
   * Removes container from DOM
   */
  private removeContainer(container: HTMLElement): void {
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }

  /**
   * Waits for content rendering (fonts, layout, etc.)
   */
  private async waitForRendering(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 1000));
  }

  /**
   * Captures element using html2canvas with optimal settings
   */
  private async captureWithHtml2Canvas(container: HTMLElement): Promise<HTMLCanvasElement> {
    const html2canvas = await import('html2canvas');
    
    return html2canvas.default(container, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: true,
      width: container.offsetWidth,
      height: container.offsetHeight
    });
  }

  /**
   * Downloads canvas as file
   */
  private downloadCanvas(canvas: HTMLCanvasElement, filename: string): void {
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
